import { useEffect, useState } from "react";
import {
  Alert,
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  Typography,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import { axiosInstance } from "../../utils/axiosInstance";
import type { HelpdeskCustomerProfileResponse } from "./helpdeskTypes";

type Props = {
  open: boolean;
  customerId: string | null;
  onClose: () => void;
};

export default function HelpdeskCustomerProfileDialog({ open, customerId, onClose }: Props) {
  const [profile, setProfile] = useState<HelpdeskCustomerProfileResponse | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!open || !customerId) {
      setProfile(null);
      setError(null);
      return;
    }
    let cancelled = false;
    (async () => {
      setLoading(true);
      setError(null);
      try {
        const res = await axiosInstance.get<HelpdeskCustomerProfileResponse>(
          `/customer-service/helpdesk/customers/${encodeURIComponent(customerId)}`
        );
        if (!cancelled) setProfile(res.data);
      } catch (err: unknown) {
        const msg =
          (err as { response?: { data?: { message?: string } } })?.response?.data?.message ||
          (err as Error)?.message ||
          "Failed to load customer.";
        if (!cancelled) {
          setError(msg);
          setProfile(null);
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [open, customerId]);

  return (
    <Dialog open={open} onClose={onClose} fullWidth maxWidth="sm">
      <DialogTitle>
        Customer profile
        <IconButton aria-label="close" onClick={onClose} size="small">
          <CloseIcon />
        </IconButton>
      </DialogTitle>
      <DialogContent dividers>
        {loading && <Typography variant="body2">Loading…</Typography>}
        {error && (
          <Alert severity="error" sx={{ mb: 1 }}>
            {error}
          </Alert>
        )}
        {profile && (
          <Box display="flex" flexDirection="column" gap={1.5}>
            <Typography variant="subtitle2" color="text.secondary">
              Display name
            </Typography>
            <Typography variant="body1">{profile.customer.displayName}</Typography>
            {profile.customer.normalizedEmail && (
              <>
                <Typography variant="subtitle2" color="text.secondary">
                  Email
                </Typography>
                <Typography variant="body2">{profile.customer.normalizedEmail}</Typography>
              </>
            )}
            {profile.customer.normalizedPhone && (
              <>
                <Typography variant="subtitle2" color="text.secondary">
                  Phone (normalized digits)
                </Typography>
                <Typography variant="body2">{profile.customer.normalizedPhone}</Typography>
              </>
            )}
            <Typography variant="subtitle2" color="text.secondary" sx={{ mt: 1 }}>
              Platform identities
            </Typography>
            {profile.identities.length === 0 ? (
              <Typography variant="body2">None linked yet.</Typography>
            ) : (
              <Box component="ul" sx={{ m: 0, pl: 2 }}>
                {profile.identities.map((i) => (
                  <li key={i.id}>
                    <Typography variant="body2">
                      {i.source} · {i.externalId}
                    </Typography>
                  </li>
                ))}
              </Box>
            )}
            <Typography variant="subtitle2" color="text.secondary" sx={{ mt: 1 }}>
              Linked tickets (recent)
            </Typography>
            {profile.linkedTickets.length === 0 ? (
              <Typography variant="body2">None in local store yet.</Typography>
            ) : (
              <Box component="ul" sx={{ m: 0, pl: 2 }}>
                {profile.linkedTickets.map((t, idx) => (
                  <li key={`${t.source}-${t.externalId}-${idx}`}>
                    <Typography variant="body2">
                      {t.source}:{t.externalId}
                    </Typography>
                  </li>
                ))}
              </Box>
            )}
          </Box>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Close</Button>
      </DialogActions>
    </Dialog>
  );
}
